// Board: сетка 3×3 целиком. Владеет ячейками, выбранной ячейкой, форматом и темой,
// сохранением и загрузкой обложек. UI только читает состояние, вызывает операции и перерисовывается
// по событиям.
//
// Interface (всё, что нужно знать вызывающему):
//   createBoard({ storage, loadImage, preload?, saveDelayMs? }) → board
//     storage:   { load() → объект|null, save(obj) → boolean }       (adapter: localStorage / память)
//     loadImage: (url) → Promise<dataUrl>                            (adapter: сеть / заглушка)
//     preload:   (dataUrls[]) → Promise                              (по умолчанию ничего не ждёт)
//   Чтение:  board.mode, board.theme, board.sel, board.doc ({title, cells[9]}), board.generation, board.snapshot()
//   Операции: select(i) · setMode(id) · setTheme(t) · setTitle(text) · edit({title?, sub?})
//             place(candidate, {advance}) → Promise<'none'|'ok'|'stale'|'failed'>
//             attachCover({url}|{data}, idx?, {silent}) → Promise<'ok'|'stale'|'failed'>
//             removeCover() · clearCell() · clear() · replaceCells(items) · flush()
//   События (subscribe(fn) → unsubscribe): sel, mode, theme, title, cell (набор текста), place, cover,
//             cover-failed {replacing}, reset, save-failed.
//
// Инварианты:
//  • Обложка, загруженная для ячейки, применяется, только если содержимое ячейки не заменили за время
//    загрузки (place, clearCell, clear, replaceCells, смена названия через edit). Иначе результат 'stale'.
//  • Правило выбора — одно: place({advance:true}) переводит выбор к следующей пустой ячейке по кругу;
//    если все заняты, выбор остаётся на месте.
//  • Сохранение отложенное (saveDelayMs), flush() пишет сразу. Об ошибке записи сообщается один раз.

import { MODE_IDS, N, normalize, emptyCell } from './state.js';

const isEmpty = (c) => !c.title && !c.sub && !c.img;

export function createBoard({ storage, loadImage, preload = async () => {}, saveDelayMs = 250 }) {
  const s = normalize(storage.load());
  const revs = Object.fromEntries(MODE_IDS.map((m) => [m, Array(N).fill(0)]));
  const subs = new Set();
  let timer = null;
  let saveWarned = false;
  let generation = 0;

  const emit = (ev) => { for (const f of [...subs]) f(ev); };
  const doc = () => s.docs[s.mode];

  function persist() {
    clearTimeout(timer);
    timer = setTimeout(flush, saveDelayMs);
    if (timer && typeof timer.unref === 'function') timer.unref();   // в Node отложенная запись не удерживает процесс
  }
  function flush() {
    clearTimeout(timer);
    if (!storage.save({ mode: s.mode, theme: s.theme, docs: s.docs }) && !saveWarned) {
      saveWarned = true;
      emit({ kind: 'save-failed' });
    }
  }
  const bump = (idx) => { revs[s.mode][idx]++; };

  function advanceFrom(idx) {
    const cells = doc().cells;
    for (let k = 1; k < cells.length; k++) {
      const j = (idx + k) % cells.length;
      if (isEmpty(cells[j])) return j;
    }
    return idx;
  }

  async function attachCover(source, idx = s.sel, { silent = false } = {}) {
    const mode = s.mode;
    const myRev = revs[mode][idx];
    const cell = s.docs[mode].cells[idx];
    const replacing = !!cell.img;
    try {
      const data = source.data || await loadImage(source.url);
      if (revs[mode][idx] !== myRev) return 'stale';   // ячейку заменили, пока грузилась картинка
      s.docs[mode].cells[idx].img = data;
      await preload([data]);
      persist();
      emit({ kind: 'cover', idx });
      return 'ok';
    } catch {
      if (!silent) emit({ kind: 'cover-failed', idx, replacing });
      return 'failed';
    }
  }

  const board = {
    get mode() { return s.mode; },
    get theme() { return s.theme; },
    get sel() { return s.sel; },
    get doc() { return doc(); },
    get generation() { return generation; },
    snapshot() { return JSON.parse(JSON.stringify({ mode: s.mode, theme: s.theme, sel: s.sel, docs: s.docs })); },
    subscribe(fn) { subs.add(fn); return () => subs.delete(fn); },
    flush,

    select(i) {
      if (!Number.isInteger(i) || i < 0 || i >= N) return;
      s.sel = i;
      persist();
      emit({ kind: 'sel' });
    },
    setMode(id) {
      if (!MODE_IDS.includes(id) || id === s.mode) return;
      s.mode = id;
      persist();
      emit({ kind: 'mode' });
    },
    setTheme(t) {
      s.theme = t === 'light' ? 'light' : 'dark';
      persist();
      emit({ kind: 'theme' });
    },
    setTitle(text) {
      doc().title = String(text).slice(0, 80);
      persist();
      emit({ kind: 'title' });
    },
    // набор текста в полях выбранной ячейки; смена названия отменяет ожидающую обложку
    edit(patch) {
      const cell = doc().cells[s.sel];
      if ('title' in patch && patch.title !== cell.title) bump(s.sel);
      Object.assign(cell, patch);
      persist();
      emit({ kind: 'cell', idx: s.sel });
    },
    // выбрать кандидата (из поиска, подсказок, «как есть») в выбранную ячейку
    place(candidate, { advance = false } = {}) {
      const idx = s.sel;
      doc().cells[idx] = { title: candidate.title, sub: candidate.sub || '', img: '' };
      bump(idx);
      if (advance) s.sel = advanceFrom(idx);
      persist();
      emit({ kind: 'place', idx, advanced: s.sel !== idx });
      return candidate.imgUrl ? attachCover({ url: candidate.imgUrl }, idx) : Promise.resolve('none');
    },
    attachCover,
    removeCover() {
      doc().cells[s.sel].img = '';
      persist();
      emit({ kind: 'cover', idx: s.sel });
    },
    clearCell() {
      doc().cells[s.sel] = emptyCell();
      bump(s.sel);
      persist();
      emit({ kind: 'reset' });
    },
    clear() {
      doc().cells = Array.from({ length: N }, emptyCell);
      for (let i = 0; i < N; i++) bump(i);
      generation++;
      persist();
      emit({ kind: 'reset' });
    },
    // items: [{title, sub}] — пример; картинки добавляют потом через attachCover
    replaceCells(items) {
      doc().cells = Array.from({ length: N }, (_, i) => (items[i] ? { title: items[i].title, sub: items[i].sub || '', img: '' } : emptyCell()));
      for (let i = 0; i < N; i++) bump(i);
      generation++;
      persist();
      emit({ kind: 'reset' });
    },
  };
  return board;
}
